import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { ArrowRightLeft, Loader2, X, AlertTriangle } from "lucide-react";
import { eur } from "@/lib/format";

const GIORNI_FINESTRA = 60;

export default function TrasformaPraticaDialog({ pratica, onClose }) {
  const collegata = !pratica.puo_trasformare;
  const nav = useNavigate();
  const [tipoId, setTipoId] = useState("");
  const [importo, setImporto] = useState("");
  const [sposta, setSposta] = useState(true);
  const [busy, setBusy] = useState(false);

  const { data: tipi } = useQuery({
    queryKey: ["tipi-pratica"],
    queryFn: async () => (await api.get("/tipi-pratica")).data,
  });
  const disponibili = (tipi || []).filter((t) => t.id !== pratica.tipo_pratica_id);
  const selTipo = disponibili.find((t) => t.id === tipoId);
  const incassato = collegata ? 0 : Number(pratica.importo_pagato || 0);

  const ultimoPag = (pratica.pagamenti || []).map((p) => p.timestamp).sort().slice(-1)[0];
  const giorni = ultimoPag ? Math.floor((Date.now() - new Date(ultimoPag).getTime()) / 86400000) : null;
  const fuoriFinestra = giorni != null && giorni > GIORNI_FINESTRA;

  const scegliTipo = (id) => {
    setTipoId(id);
    const t = disponibili.find((x) => x.id === id);
    if (t && !importo) setImporto(String(t.importo_default ?? ""));
  };

  const conferma = async () => {
    if (!tipoId) return toast.error("Scegli il tipo di pratica di destinazione");
    setBusy(true);
    try {
      const { data } = await api.post(`/pratiche/${pratica.id}/trasforma`, {
        tipo_pratica_id: tipoId, importo_totale: importo === "" ? null : Number(importo), sposta_acconti: sposta,
      });
      toast.success(`${collegata ? "Pratica collegata creata" : "Trasformata"}: ${data.tipo}${data.importo_scalato ? ` · ${eur(data.importo_scalato)} scalati come acconto` : ""}`);
      if ((data.campi_mancanti || []).length) {
        toast.warning(`Da completare nell'anagrafica: ${data.campi_mancanti.join(", ")}`, { duration: 9000 });
      }
      onClose();
      nav(`/pratiche/${data.pratica_id}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Trasformazione non riuscita"); }
    finally { setBusy(false); }
  };

  const inputCls = "w-full mt-1 px-3 py-2 bg-white border border-[#1A2B4A]/15 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#C9A84C]/40";
  const labelCls = "text-[10px] font-medium uppercase tracking-[0.12em] text-[#8a93a6]";

  return (
    <div className="fixed inset-0 z-[70] bg-[#16233c]/40 backdrop-blur-sm flex items-center justify-center p-4" data-testid="trasforma-dialog">
      <div className="bg-white rounded-[1.5rem] w-full max-w-lg p-5 border-t-2 border-[#C9A84C] shadow-[0_20px_40px_rgba(0,0,0,0.12)] max-h-[90vh] overflow-y-auto">
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-2">
            <ArrowRightLeft className="w-4 h-4 text-[#C9A84C]" />
            <h3 className="text-2xl font-semibold tracking-tight text-[#1A2B4A]">{collegata ? "Apri pratica collegata" : "Trasforma in pratica"}</h3>
          </div>
          <button data-testid="trasforma-close" onClick={onClose} className="text-[#1A2B4A] hover:text-[#9b2c2c]"><X className="w-5 h-5" /></button>
        </div>

        <p className="text-[12px] text-[#4a5568] mb-4">
          {collegata
            ? <>La nuova pratica nasce sullo stesso cliente con i documenti del cliente collegati. Questa <b>{pratica.tipo_pratica_nome}</b> resta aperta e invariata, senza spostamenti di importi.</>
            : <>La nuova pratica nasce sullo stesso cliente e si porta dietro dati raccolti, note del diario, documenti e contabilità. Questa <b>{pratica.tipo_pratica_nome}</b> risulterà conclusa e non comparirà più nell'elenco pratiche.</>}
        </p>

        <label className={labelCls}>Tipo di pratica di destinazione</label>
        <select data-testid="trasforma-tipo" value={tipoId} onChange={(e) => scegliTipo(e.target.value)} className={inputCls}>
          <option value="">Scegli…</option>
          {disponibili.map((t) => <option key={t.id} value={t.id}>{t.nome}</option>)}
        </select>

        <div className="mt-3">
          <label className={labelCls}>Valore della nuova pratica (€)</label>
          <input data-testid="trasforma-importo" type="number" value={importo}
            onChange={(e) => setImporto(e.target.value)} className={inputCls} />
        </div>

        {incassato > 0 && (
          <label className="flex items-start gap-2 mt-4 cursor-pointer select-none">
            <input data-testid="trasforma-sposta" type="checkbox" checked={sposta}
              onChange={(e) => setSposta(e.target.checked)} className="w-4 h-4 accent-[#C9A84C] mt-0.5" />
            <span className="text-[12px] text-[#1A2B4A]">
              Scala <b>{eur(incassato)}</b> già incassati come acconto della nuova pratica
              <span className="block text-[11px] text-[#4a5568]">
                L'incasso viene spostato: nessun doppio conteggio negli onorari.
              </span>
            </span>
          </label>
        )}

        {incassato > 0 && fuoriFinestra && sposta && (
          <div data-testid="trasforma-avviso-finestra" className="mt-3 rounded-md border border-[#C9A84C]/40 bg-[#fbf6e8] px-3 py-2">
            <p className="text-[11px] text-[#8a6d1f] flex items-start gap-1.5">
              <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
              Sono passati {giorni} giorni dal pagamento: oltre i {GIORNI_FINESTRA} giorni dichiarati sul sito.
              Puoi scalarlo comunque, decidi tu.
            </p>
          </div>
        )}

        {selTipo && (
          <p className="text-[11px] text-[#4a5568] mt-4">
            Verranno applicati gli step, i campi obbligatori e la checklist documenti di <b>{selTipo.nome}</b>.
            Se qualche dato obbligatorio manca, la pratica si crea comunque e ti indico cosa completare.
          </p>
        )}

        <div className="flex justify-end gap-3 mt-6">
          <button onClick={onClose} disabled={busy}
            className="px-4 py-2 text-sm border border-[#1A2B4A] rounded-md hover:bg-[#f9f8f5] transition-colors">Annulla</button>
          <button data-testid="trasforma-conferma" onClick={conferma} disabled={busy}
            className="px-5 py-2 text-sm bg-[#1A2B4A] text-white rounded-md font-semibold hover:bg-[#22345c] inline-flex items-center gap-2 disabled:opacity-60">
            {busy && <Loader2 className="w-4 h-4 animate-spin" />} {busy ? "Creo…" : (collegata ? "Apri pratica" : "Trasforma")}
          </button>
        </div>
      </div>
    </div>
  );
}
